import { KEYS, SUPPORTED_LENGTHS, readJson, writeJson, type StorageAdapter } from '@lexicon/core';

/** Playable lengths (spec §10.1), derived so it cannot drift. */
const WORD_RE = new RegExp(
  `^[a-z]{${Math.min(...SUPPORTED_LENGTHS)},${Math.max(...SUPPORTED_LENGTHS)}}$`,
);

/** A challenge this device created. Offline-only links are not stored. */
export interface MyChallenge {
  code: string;
  word: string;
  createdAt: number;
  challengerName?: string;
}

const MAX_STORED = 50;

function isMyChallenge(v: unknown): v is MyChallenge {
  if (v === null || typeof v !== 'object') return false;
  const c = v as Record<string, unknown>;
  return typeof c.code === 'string'
    && typeof c.word === 'string'
    && WORD_RE.test(c.word)
    && typeof c.createdAt === 'number';
}

/** Newest first. Corrupt entries are dropped, never thrown (spec §7). */
export async function loadMyChallenges(storage: StorageAdapter): Promise<MyChallenge[]> {
  const raw = await readJson<unknown>(storage, KEYS.myChallenges, null);
  if (!Array.isArray(raw)) return [];
  return raw.filter(isMyChallenge).sort((a, b) => b.createdAt - a.createdAt);
}

/** Prepend (de-duplicating by code) and persist, keeping the newest 50. */
export async function addMyChallenge(
  storage: StorageAdapter,
  challenge: MyChallenge,
): Promise<MyChallenge[]> {
  const existing = await loadMyChallenges(storage);
  const next = [challenge, ...existing.filter((c) => c.code !== challenge.code)]
    .slice(0, MAX_STORED);
  await writeJson(storage, KEYS.myChallenges, next);
  return next;
}
